import React, { useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";

/* ===================== PDF.JS LOADER ===================== */
const loadPdfJs = () => {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://sironline.in/vendor/build/pdf.js";
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://sironline.in/vendor/build/pdf.worker.js";
      resolve(window.pdfjsLib);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/* ===================== COMPONENT ===================== */
const DocumentPreviewModal = ({
  isOpen,
  onClose,
  fileUrl,
  signatures = []
}) => {
  const containerRef = useRef(null);   // SAME container for PDF & DOCX
  const canvasRef = useRef(null);      // PDF canvas
  const docxRef = useRef(null);        // DOCX render target
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const isPdf = fileUrl?.toLowerCase().endsWith(".pdf");
  const isDocx =
    fileUrl?.toLowerCase().endsWith(".docx") ||
    fileUrl?.toLowerCase().endsWith(".doc");

  /* ===================== PDF RENDER ===================== */
  const renderPdfPage = async (pdf, pageNumber) => {
    if (!canvasRef.current) return;

    const page = await pdf.getPage(pageNumber);
    const scale = 1.25;
    const viewport = page.getViewport({ scale });

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: ctx,
      viewport
    }).promise;
  };

  const renderPdf = async (url) => {
    const pdfjsLib = await loadPdfJs();
    const pdf = await pdfjsLib.getDocument(url).promise;
    
    setPdfDoc(pdf);
    setTotalPages(pdf.numPages);
    setPageNum(1);
    
    await renderPdfPage(pdf, 1);
    setDocumentLoaded(true);
  };

  const goToPage = async (newPageNum) => {
    if (!pdfDoc || newPageNum < 1 || newPageNum > totalPages) return;
    
    setPageNum(newPageNum);
    await renderPdfPage(pdfDoc, newPageNum);
  };

  /* ===================== EFFECT ===================== */
  useEffect(() => {
    if (!isOpen || !fileUrl) {
      setDocumentLoaded(false);
      return;
    }

    setDocumentLoaded(false);

    if (isPdf) {
      renderPdf(fileUrl);
    }

    if (isDocx) {
      fetch(fileUrl)
        .then(res => res.arrayBuffer())
        .then(buffer => {
          docxRef.current.innerHTML = "";
          renderAsync(buffer, docxRef.current).then(() => {
            setDocumentLoaded(true);
          });
        });
    }
  }, [fileUrl, isOpen]);

  if (!isOpen || !fileUrl) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          width: "90%",
          maxWidth: "900px",
          height: "90vh",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column"
        }}
      >

        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <strong>Document Preview</strong>
          
          {/* PDF Pagination Controls */}
          {isPdf && totalPages > 1 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              fontSize: '14px'
            }}>
              <button
                onClick={() => goToPage(pageNum - 1)}
                disabled={pageNum <= 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: pageNum <= 1 ? '#f3f4f6' : '#10b981',
                  color: pageNum <= 1 ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '0',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  cursor: pageNum <= 1 ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  minWidth: '60px'
                }}
              >
                Prev
              </button>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#374151',
                color: 'white',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '700',
                minWidth: '100px',
                justifyContent: 'center',
                borderTop: '2px solid #10b981',
                borderBottom: '2px solid #10b981'
              }}>
                {pageNum} of {totalPages}
              </div>
              
              <button
                onClick={() => goToPage(pageNum + 1)}
                disabled={pageNum >= totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: pageNum >= totalPages ? '#f3f4f6' : '#10b981',
                  color: pageNum >= totalPages ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '0',
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px',
                  cursor: pageNum >= totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  minWidth: '60px'
                }}
              >
                Next
              </button>
            </div>
          )}
          
          <button onClick={onClose} style={{ 
            padding: "8px 12px", 
            backgroundColor: "#dc2626", 
            color: "white", 
            border: 'none', 
            borderRadius: '8px', 
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
          }}>✕ Close</button>
        </div>

        <div
          ref={containerRef}
          style={{
            flex: 1,
            overflow: "auto",
            position: "relative",
            padding: "20px"
          }}
        >
          {/* -------- PDF -------- */}
          {isPdf && (
            <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
              <canvas
                ref={canvasRef}
                style={{
                  display: "block",
                  maxWidth: "100%",
                  height: "auto"
                }}
              />
              {/* PDF Signatures - Page Specific */}
              {documentLoaded && signatures.filter(sig => !sig.page || sig.page === pageNum).map((sig) => (
                <div
                  key={sig.id}
                  style={{
                    position: "absolute",
                    left: sig.x -50,
                    top: sig.y -20,
                    zIndex: 50,
                    pointerEvents: "none"
                  }}
                >
                  {sig.imageUrl ? (
                    <img
                      src={sig.imageUrl}
                      alt="Signature"
                      style={{
                        width: `${sig.width}px`,
                        height: `${sig.height}px`,
                        objectFit: "contain",
                        mixBlendMode: "multiply"
                      }}
                    />
                  ) : sig.text && (
                    <div
                      style={{
                        // fontSize: "24px",
                        // fontWeight: "bold",
                        color: sig.color || "#000",
                        fontFamily: "Brush Script MT, cursive",
                        fontStyle: "italic"
                      }}
                    >
                      {sig.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* -------- DOCX -------- */}
          {isDocx && (
            <div style={{ position: "relative" }}>
              <div
                ref={docxRef}
                className="docx-preview-container"
                style={{
                  background: "white"
                }}
              />
              {/* DOCX Signatures - Page Specific */}
              {documentLoaded && signatures.filter(sig => !sig.page || sig.page === 1).map((sig) => (
                <div
                  key={sig.id}
                  style={{
                    position: "absolute",
                    left: sig.x - 40,
                    top: sig.y - 20,
                    zIndex: 50,
                    pointerEvents: "none"
                  }}
                >
                  {sig.imageUrl ? (
                    <img
                      src={sig.imageUrl}
                      alt="Signature"
                      style={{
                        width: `${sig.width}px`,
                        height: `${sig.height}px`,
                        objectFit: "contain",
                        mixBlendMode: "multiply"
                      }}
                    />
                  ) : sig.text && (
                    <div
                      style={{
                        // fontSize: "24px",
                        // fontWeight: "bold",
                        color: sig.color || "#000",
                        fontFamily: "Brush Script MT, cursive",
                        fontStyle: "italic"
                      }}
                    >
                      {sig.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
